import React, { useEffect, useState } from "react";
import { Camera, ArrowLeft, Monitor, ShieldCheck, Users, } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { showError } from "../../../utils/toast";
import baseapi from "../../../services/baseapi";
import CameraOverview from "./CameraOverview";
import CameraInterface from "./CameraInterface";
import CameraProtocols from "./CameraProtocols";
import CameraUsers from "./CameraUsers";
import CameraDynamicDetails from "./CameraDynamicDetails";

const CompactCameraDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [counts, setCounts] = useState({
        interfaces: null as number | null,
        protocols: null as number | null,
        users: null as number | null
    });
    const [fingerprintMode, setFingerprintMode] = useState(false);
    const [dynamicData, setDynamicData] = useState<any>(null);
    const info = JSON.parse(localStorage.getItem("internal_ids") || "{}");
    const [loading, setLoading] = useState(true);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: info.asset_id, client_id: info.client_id }),
            };
            const response = await baseapi.get("/asset_camera_details", { params });
            if (response.data.status && response.data.data?.length > 0) {
                const data = response.data.data[0];
                if (!data.auth_success) {
                    setFingerprintMode(true);
                }
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, []);

    const hostname = info.host_name || info.ip_address || "Camera System";
    const hasInterfaces = counts.interfaces === null || counts.interfaces > 0;
    const hasProtocols = counts.protocols === null || counts.protocols > 0;
    const hasUsers = counts.users === null || counts.users > 0;

    const getTopCardClass = (type: 'interfaces' | 'protocols') => {
        const isOnlyOne = (type === 'interfaces' && !hasProtocols) || (type === 'protocols' && !hasInterfaces);
        return `flex flex-col overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm ${isOnlyOne ? 'col-span-1 xl:col-span-2' : 'col-span-1'}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#422568]"></div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col gap-3 p-3 bg-slate-50 text-slate-600 transition-all duration-500 ${fingerprintMode ? 'h-screen overflow-hidden' : 'min-h-screen pb-10'}`}>
            {/* Header */}
            <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap border-b border-slate-200 pb-2">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#422568] rounded-lg shadow-lg shadow-[#422568]/20">
                        <Camera className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-slate-900 tracking-tight leading-none mb-0.5">Camera Dashboard</h1>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Hostname : {hostname}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 mr-2 shadow-sm">
                        <div className={`p-1.5 rounded-md transition-colors ${fingerprintMode ? 'bg-[#422568] text-white' : 'text-slate-400'}`}>
                            <Fingerprint className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Fingerprint</span>
                        <button
                            onClick={() => setFingerprintMode(!fingerprintMode)}
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 focus:outline-none ${fingerprintMode ? 'bg-[#422568]' : 'bg-slate-200'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${fingerprintMode ? 'translate-x-5.5' : 'translate-x-1'}`} />
                        </button>
                    </div> */}

                    <button
                        onClick={() => navigate(info.path || "/asset")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#422568] rounded-lg text-[11px] font-bold text-white hover:bg-[#422568] hover:text-white transition-all active:scale-95 shadow-sm uppercase tracking-wide"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back
                    </button>
                </div>
            </div>

            {/* Top Row: Overview Stats */}
            {!fingerprintMode && (
                <div className="w-full">
                    <CameraOverview onDynamicDataLoaded={setDynamicData} />
                </div>
            )}

            {/* Grid for Tables View */}
            {!fingerprintMode && (
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {/* Network Interfaces */}
                        {hasInterfaces && (
                            <div className={getTopCardClass('interfaces')}>
                                <div className="px-3 py-1.5 flex items-center justify-between border-b border-white bg-slate-50/50">
                                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                        <Monitor className="w-3.5 h-3.5 text-emerald-500" />
                                        Interfaces
                                    </h3>
                                </div>
                                <div className="p-1">
                                    <CameraInterface
                                        onDataLoaded={(count: number) => setCounts(prev => ({ ...prev, interfaces: count }))}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Protocols */}
                        {hasProtocols && (
                            <div className={getTopCardClass('protocols')}>
                                <div className="px-3 py-1.5 flex items-center justify-between border-b border-white bg-slate-50/50">
                                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                        <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
                                        Protocols
                                    </h3>
                                </div>
                                <div className="p-1">
                                    <CameraProtocols
                                        onDataLoaded={(count: number) => setCounts(prev => ({ ...prev, protocols: count }))}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 xl:grid-cols-1 gap-3 col-span-1 xl:col-span-2">
                            {/* Users / Access Control */}
                            {hasUsers && (
                                <div className="overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm">
                                    <div className="px-3 py-1.5 flex items-center justify-between border-b border-white bg-slate-50/50">
                                        <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <Users className="w-3.5 h-3.5 text-purple-500" />
                                            Users
                                        </h3>
                                    </div>
                                    <div className="p-1">
                                        <CameraUsers
                                            onDataLoaded={(count: number) => setCounts(prev => ({ ...prev, users: count }))}
                                        />
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* Dynamic Telemetry Data */}
                    {dynamicData && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 pb-12">
                            <CameraDynamicDetails data={dynamicData} />
                        </div>
                    )}
                </div>
            )}

            {/* Fingerprint Mode (Mocked or Placeholder) */}
            {fingerprintMode && (
                <div className="flex-1 flex items-center justify-center text-slate-400 font-bold uppercase text-xs">
                    Authentication mismatch detected. Please review camera credentials.
                </div>
            )}
        </div>
    );
};

export default CompactCameraDashboard;
